// ─────────────────────────────────────────────
//  RailEngineSwitch — is the Google Sheet engine running, and stop it.
//
//  Owner, 2026-09-09, having started the engine: "I don't see any changes in
//  the page after engine is running" — and then, pointing at the trust block:
//  "add here when the system is working and place an on and off switch to stop
//  the engine manually".
//
//  Both halves of that are the same problem. An unattended process writing to
//  a live OR board is invisible until it writes something wrong, and the only
//  control for it lived three clicks away in Setup. A board-runner watching the
//  timeline could not tell whether the sheet was being synced at all, and could
//  not stop it from where they were standing.
//
//  ⚠️ IT DOES NOT REPEAT THE TRUST BLOCK'S COLOUR LANGUAGE. That block's GROUND
//  colour already means one thing — whether this browser's data is fresh — and
//  it turns red when the board stops being trustworthy. Two independent things
//  colouring the same panel would make both unreadable, so the engine speaks in
//  a small bordered pill inside it and never touches the ground. They are
//  genuinely different facts: the board can be perfectly live while the engine
//  is dead, and vice versa.
//
//  ⚠️ STATE COMES FROM domain/sheetEngine.js, THE SAME MODULE THE SETUP PANEL
//  USES. Two surfaces now show this, and a second opinion about what "running"
//  means is how they would come to disagree in front of somebody deciding
//  whether to trust the board.
//
//  Owner, 2026-10-01, app not in use: "put a button ... ATC app on and off so
//  ... we can shut down the engine from there". The SAME stored switch now also
//  gates the nightly sheet import and the hourly test-session sweep (see
//  domain/atcAppSwitch.js), so this button is labelled for what it now does.
// ─────────────────────────────────────────────
import React from "react";

import { SHEET_ENGINE_STATUS } from "../../domain/sheetEngine";

// Tone → pill colours, on the trust block's dark ground. Deliberately muted:
// this is a status line, not an alarm. The one state that earns saturated red
// is STALLED, because "ON but nothing has checked in" is the state where the
// board silently stops following the sheet and nobody is told.
const TONE = {
  neutral: { bg: "#1e2436", ink: "#9aa4bf", border: "#2f3852" },
  ok:      { bg: "#062e24", ink: "#6ee7b7", border: "#0f5132" },
  warn:    { bg: "#5b3a09", ink: "#fcd34d", border: "#8a5a0f" },
  bad:     { bg: "#7f1d1d", ink: "#fecaca", border: "#b91c1c" },
};

// The short word the pill prints. `state.label` is a sentence written for the
// Setup panel, which has room for it; a 179px rail does not.
const WORD = {
  [SHEET_ENGINE_STATUS.RUNNING]:  "SYNCING",
  [SHEET_ENGINE_STATUS.STARTING]: "STARTING",
  [SHEET_ENGINE_STATUS.STALLED]:  "NOT RUNNING",
  [SHEET_ENGINE_STATUS.ERRORING]: "ERROR",
  [SHEET_ENGINE_STATUS.STOPPING]: "STOPPING",
  [SHEET_ENGINE_STATUS.OFF]:      "OFF",
};

export default function RailEngineSwitch({
  state = null,
  canToggle = false,
  busy = false,
  onToggle,
}) {
  // Absent rather than empty. A facility with no engine configured should not
  // grow a permanent "OFF" line on its rail explaining a feature it does not
  // have — see the house rule about refusals that were never on offer.
  if (!state) return null;

  const tone = TONE[state.tone] || TONE.neutral;
  const word = WORD[state.status] || "UNKNOWN";
  const on = state.status !== SHEET_ENGINE_STATUS.OFF
    && state.status !== SHEET_ENGINE_STATUS.STOPPING;

  // ⚠️ ON ASKS, OFF DOES NOT. Arming an unattended writer against a live board
  // deserves a question; switching it off is the safe direction and is what
  // somebody reaches for when the sheet is wrong, so it must never be slowed
  // down. Same rule as the Setup panel, so the two behave identically.
  const handle = () => {
    if (busy || !onToggle) return;
    if (on) { onToggle(false); return; }
    const ok = window.confirm(
      "Turn the ATC app ON?\n\nThe Google Sheet engine will start applying changes from the tracking sheet to this live board automatically, and the nightly sheet import and hourly sweep resume."
    );
    if (ok) onToggle(true);
  };

  const detail = state.detail || "";
  const age = state.ageLabel ? ` · ${state.ageLabel}` : "";

  return (
    <div style={s.wrap} data-testid="rail-engine" data-status={state.status}>
      <div
        style={{ ...s.pill, background: tone.bg, color: tone.ink, borderColor: tone.border }}
        // The pill is the status; assistive tech reads the sentence, not the
        // abbreviation, because "SYNCING" alone does not say what is syncing.
        role="status"
        aria-label={`ATC app, Google Sheet engine: ${state.label}`}
        title={detail}
      >
        <span style={s.pillWord}>ATC&nbsp;APP · SHEET&nbsp;ENGINE</span>
        <span style={s.pillState}>{word}{age}</span>
      </div>

      {canToggle && (
        <button
          type="button"
          role="switch"
          aria-checked={on}
          aria-label={on
            ? "Turn the ATC app off: stops the sheet engine, nightly import and sweep"
            : "Turn the ATC app on: starts the sheet engine, nightly import and sweep"}
          data-testid="rail-engine-toggle"
          disabled={busy}
          onClick={handle}
          style={{ ...s.btn, opacity: busy ? 0.6 : 1, cursor: busy ? "wait" : "pointer" }}
        >
          {busy ? "…" : on ? "TURN ATC APP OFF" : "TURN ATC APP ON"}
        </button>
      )}
    </div>
  );
}

const s = {
  wrap: { marginTop: 8, display: "grid", gap: 6 },
  pill: {
    border: "1px solid",
    borderRadius: 6,
    padding: "4px 6px",
    display: "grid",
    gap: 1,
    // The rail is 179px and this text must not push it wider.
    minWidth: 0,
  },
  pillWord: { fontSize: 8, fontWeight: 800, letterSpacing: ".08em", opacity: 0.75 },
  pillState: { fontSize: 10, fontWeight: 800, letterSpacing: ".04em" },
  btn: {
    // 44px high: pressed on a wall display, sometimes with gloves, and it is
    // the control somebody reaches for when the board is going wrong.
    minHeight: 44,
    width: "100%",
    borderRadius: 6,
    border: "1px solid #2f3852",
    background: "#141a2a",
    color: "#c7d2fe",
    font: "inherit",
    fontSize: 10,
    fontWeight: 800,
    letterSpacing: ".06em",
  },
};
